import React from 'react';
import clsx from 'clsx';
import { SkeletonCardFields } from 'components/Loading';
import { makeStyles } from '@material-ui/styles';
import { Add, Delete, ZoomOutMap } from '@material-ui/icons';
import {
  Button,
  IconButton,
  Grid,
  Box,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography
} from '@material-ui/core';
import { actions } from 'store/attachmentTemplate';
import useTemplate from 'views/AttachmentTemplates/useTemplate';
import { Card, CardHeader, CardContent, CardActions, Tooltip } from '@material-ui/core';
import { toTemplateDropdownOptions } from 'views/Campaign/helpers';
import { useDispatch } from 'react-redux';
import { Details, Design } from 'views/AttachmentTemplates/components';
import CloseIcon from '@material-ui/icons/Close';
import { unsetCurrentAttachmentTemplate } from 'store/attachmentTemplate/actions';
import TemplateSingleSelect from 'components/TemplateSingleSelect';

const { unsetNextStep } = actions;

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: theme.spacing(3)
  },
  selectContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1)
  },
  buttonContainer: {
    display: 'inline',
    margin: theme.spacing(1)
  },
  button: {
    margin: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  openButton: {
    color: theme.palette.primary.main
  },
  dialogSubTitle: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#546e7a',
    letterSpacing: '-0.05px'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  nextButton: {
    marginRight: '16px',
    marginBottom: '8px'
  }
}));

const Attachments = props => {
  const {
    className,
    staticContext,
    campaign,
    next,
    back,
    templates,
    showNav,
    paginatedTemplates,
    attachmentFilters,
    isAttachmentDropdownLoading,
    isAttachmentNameHydrationLoading,
    attachmentSelectedNamesByUuid,
    attachmentTags,
    ...rest
  } = props;
  const {
    thisCampaign,
    goNext,
    goBack,
    shouldShowError,
    getErrorMessage,
    onBlur,
    onFocus,
    isLoading,
    addAttachment,
    updateAttachment,
    removeAttachment,
    isCampaignReadOnly
  } = campaign;

  const [open, setOpen] = React.useState(false);
  const template = useTemplate(undefined, undefined, open);
  const { isInTemplateDesignDialog } = template;

  const classes = useStyles();
  const dispatch = useDispatch();
  const hierarchyFilterOptions = [
    {
      value: 'global',
      searchScope: 'GLOBAL DEFAULT'
    },
    {
      value: 'tenant',
      searchScope: 'TENANT DEFAULT'
    },
    {
      value: 'property',
      searchScope: 'PROPERTY LEVEL'
    }
  ];
  const tagFilterOptions = (attachmentTags || []).map(tag => ({
    label: tag,
    value: tag
  }));
  const toOptions =
    typeof toTemplateDropdownOptions === 'function'
      ? toTemplateDropdownOptions
      : values => values;
  const availablePaginatedTemplates = Array.isArray(paginatedTemplates)
    ? paginatedTemplates
    : templates;

  const openCreateAttachmentDialog = () => {
    dispatch(unsetCurrentAttachmentTemplate());
    template.resetValidationErrors();
    campaign.setCurrentAttachmentTemplateDetails();
    dispatch(actions.getAttachmentTemplate({}));
    setOpen(true);
  };

  const closeCreateAttachmentDialog = () => {
    setOpen(false);
    template.resetValidationErrors();
    dispatch(unsetNextStep());
    dispatch(unsetCurrentAttachmentTemplate());
  };

  const editAttachmentTemplateModel = templateUuid => {
    dispatch(actions.getAttachmentTemplate({ templateUuid }));
    setOpen(true);
  }

  const fetchPaginatedAttachmentOptions = overrides => {
    const campaignContext = {
      productType: thisCampaign.productType,
      subscriberListId: thisCampaign.subscriberListId,
      segmentedListId: thisCampaign.segmentedListId,
      eventTypeCategory: thisCampaign.eventTypeCategory,
      promotional: thisCampaign.promotional,
      campaignOccurrenceType: thisCampaign.campaignOccurrenceType
    };

    dispatch(
      actions.updateAttachmentFilters({
        filterProductNames: thisCampaign.productName ? [thisCampaign.productName] : [],
        filterProductCategories: thisCampaign.productCategory
          ? [thisCampaign.productCategory]
          : [],
        ...overrides
      })
    );
    dispatch(actions.getPaginatedAttachmentTemplates({ force: true, campaignContext }));
  };

  const handleAttachmentDropdownOpen = initialSearchInput => {
    if (!thisCampaign?.campaignOccurrenceType) {
      return;
    }

    if ((attachmentTags || []).length === 0) {
      dispatch(actions.getAttachmentTags());
    }

    fetchPaginatedAttachmentOptions({
      pageNo: 0,
      searchInput: (initialSearchInput || '').trim(),
      filterTemplates: [],
      filterTags: [],
      filterStatus: []
    });
  };

  const handleAttachmentSearchChange = searchInput => {
    fetchPaginatedAttachmentOptions({ searchInput, pageNo: 0 });
  };

  const handleAttachmentPageChange = pageNo => {
    fetchPaginatedAttachmentOptions({ pageNo });
  };

  const handleAttachmentRowsPerPageChange = rowsPerPage => {
    fetchPaginatedAttachmentOptions({ rowsPerPage, pageNo: 0 });
  };

  const handleAttachmentTagChange = filterTags => {
    fetchPaginatedAttachmentOptions({ filterTags, pageNo: 0 });
  };

  const handleAttachmentHierarchyChange = filterTemplates => {
    fetchPaginatedAttachmentOptions({ filterTemplates, pageNo: 0 });
  };

  const handleAttachmentStatusChange = filterStatus => {
    fetchPaginatedAttachmentOptions({ filterStatus, pageNo: 0 });
  };

  const getAttachments = attachment => {
    const attachmentOptions = toOptions(availablePaginatedTemplates).map(option => ({
      ...option,
      disabled: option?.active === false
    }));

    return {
      list: attachmentOptions,
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields />
        </Grid>
      </CardContent>
    );
  };

  if (thisCampaign.type === 'SMS') {
    return null;
  }

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <CardHeader
            subheader=""
            title="Attachments"
            titleTypographyProps={{ variant: 'h1' }}
          />
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              {!isCampaignReadOnly() && (
                <Button
                  variant="contained"
                  color="primary"
                  // disabled={!(thisCampaign.communicationTemplateId === undefined)}
                  className={classes.button}
                  startIcon={<Add />}
                  onClick={addAttachment}>
                  Add Attachment
                </Button>
              )}
              {!isCampaignReadOnly() && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  startIcon={<Add />}
                  onClick={openCreateAttachmentDialog}>
                  Create New Attachment
                </Button>
              )}
              {thisCampaign.attachmentTemplateUuids &&
                thisCampaign.attachmentTemplateUuids.length === 0 &&
                isCampaignReadOnly() &&
                'There are no attachments in this campaign.'}
              {!isInTemplateDesignDialog ? (
                <Dialog
                  open={open}
                  onClose={closeCreateAttachmentDialog}
                  aria-labelledby="form-dialog-title"
                  fullWidth
                  maxWidth="lg">
                  <Typography variant="h2">
                    <DialogTitle
                      id="form-dialog-title"
                      disableTypography={true}>
                      <div>Attachment Details</div>
                      <div className={classes.dialogSubTitle}>
                        * indicates a required field
                      </div>
                    </DialogTitle>
                    <IconButton
                      aria-label="close"
                      className={classes.closeButton}
                      onClick={closeCreateAttachmentDialog}>
                      <CloseIcon />
                    </IconButton>
                  </Typography>
                  <DialogContent>
                    {template.isLoading && (
                      <Box style={{ width: '50%' }}>
                        {' '}
                        <CircularProgress style={{ float: 'right' }} />
                      </Box>
                    )}
                    {!template.isLoading && (
                      <Details
                        isPopupDialog={true}
                        useCampaignAutomation={false}
                        template={template}></Details>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button
                      className={classes.nextButton}
                      onClick={template.goNext}
                      disabled={template.isLoading}
                      color="primary"
                      variant="contained">
                      Next
                    </Button>
                  </DialogActions>
                </Dialog>
              ) : (
                <Dialog
                  variant="h2"
                  open={open}
                  onClose={closeCreateAttachmentDialog}
                  aria-labelledby="form-dialog-title"
                  fullWidth
                  maxWidth="lg">
                  <Typography variant="h2">
                    <DialogTitle
                      id="form-dialog-title"
                      disableTypography={true}>
                      <div>Attachment Design</div>
                    </DialogTitle>
                    <IconButton
                      aria-label="close"
                      className={classes.closeButton}
                      onClick={closeCreateAttachmentDialog}>
                      <CloseIcon />
                    </IconButton>
                  </Typography>
                  <DialogContent>
                    <Design isPopupDialog={true} template={template}></Design>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      className={classes.backButton}
                      onClick={template.goBack}
                      disabled={template.isLoading}
                      color="secondary"
                      variant="contained">
                      Back
                    </Button>
                    <Box flexGrow={1}>&nbsp;</Box>
                  </DialogActions>
                </Dialog>
              )}

              {(thisCampaign.attachmentTemplateUuids || []).map(
                (attachment, i) => (
                  <Grid container key={attachment + i}>
                    <Grid item className={classes.selectContainer}>
                      <TemplateSingleSelect
                        fullWidth
                        label="Select an attachment"
                        disabled={isCampaignReadOnly()}
                        margin="dense"
                        name={`attachmentTemplateUuids.${i}`}
                        onChange={e => updateAttachment(e, i)}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        value={attachment || ''}
                        selectedDisplayValue={
                          templates.find(
                            templateItem => templateItem.uuid === attachment
                          )?.name ||
                          attachmentSelectedNamesByUuid?.[attachment] ||
                          ''
                        }
                        error={shouldShowError(`attachmentTemplateUuids.${i}`)}
                        helperText={getErrorMessage(
                          `attachmentTemplateUuids.${i}`
                        )}
                        options={getAttachments(attachment)}
                        remotePagination
                        totalCount={attachmentFilters?.totalCount || 0}
                        isLoading={isAttachmentDropdownLoading}
                        showFieldSkeleton={isAttachmentNameHydrationLoading}
                        hierarchyOptions={hierarchyFilterOptions}
                        selectedHierarchyValues={attachmentFilters?.filterTemplates || []}
                        selectedStatusValues={attachmentFilters?.filterStatus || []}
                        tagOptions={tagFilterOptions}
                        selectedTagValues={attachmentFilters?.filterTags || []}
                        onOpen={handleAttachmentDropdownOpen}
                        onHierarchyChange={handleAttachmentHierarchyChange}
                        onStatusChange={handleAttachmentStatusChange}
                        onTagChange={handleAttachmentTagChange}
                        onSearchChange={handleAttachmentSearchChange}
                        onPageChange={handleAttachmentPageChange}
                        onRowsPerPageChange={handleAttachmentRowsPerPageChange}
                        emptyStateText="No templates found"
                        dataTestId={`select-attachmentTemplateUuids.${i}`}
                      />
                    </Grid>

                    {attachment && (
                      <Grid item className={classes.buttonContainer}>
                        <Tooltip title="Open">
                          <IconButton
                            className={classes.openButton}
                            onClick={() => editAttachmentTemplateModel(attachment)}>
                            <ZoomOutMap />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}

                    {!isCampaignReadOnly() && (
                      <Grid item className={classes.buttonContainer}>
                        <Tooltip title="Delete">
                          <IconButton
                            className={classes.deleteButton}
                            onClick={() => removeAttachment(i)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                )
              )}
            </CardContent>
          )}
          {(next || back || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="secondary"
                  variant="contained"
                  linkTo={back}
                  onClick={goBack}>
                  Back
                </Button>
                <Box flexGrow={1}>&nbsp;</Box>
                <Button
                  color="primary"
                  variant="contained"
                  linkTo={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Attachments;
